import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { RadioStation } from '@/mocks/radios';
import { useAudioPlayer } from '@/context/AudioContext';
import { radioProfiles, getRadioTeam } from '@/mocks/radioProfile';
import { getStationLives, getStationPhotos, getStationVideos } from '@/mocks/radioContent';
import RadioFeedSection from './RadioFeedSection';
import RadioEventosSection from './RadioEventosSection';
import RadioConcursosSection from './RadioConcursosSection';
import SeccionComunidad from './SeccionComunidad';
import SeccionAcerca from './SeccionAcerca';
import ShareModal from '@/components/feature/ShareModal';
import LikeButton from '@/components/feature/LikeButton';
import {
  canPlayStream,
  shouldFallbackToRfw,
  isTerminalStatus,
  isTransientStatus,
  technicalStreamStatusShortLabels,
  technicalStreamStatusColors,
  technicalStreamStatusIcons,
} from '@/types/streaming';
import { SwalAlert } from '@/hooks/useSweetAlert';

const TABS = [
  { id: 'feed', label: 'Feed', icon: 'ri-home-5-line' },
  { id: 'schedule', label: 'Programación', icon: 'ri-calendar-2-line' },
  { id: 'content', label: 'Contenido', icon: 'ri-gallery-line' },
  { id: 'events', label: 'Eventos', icon: 'ri-calendar-event-line' },
  { id: 'contests', label: 'Concursos', icon: 'ri-trophy-line' },
  { id: 'community', label: 'Comunidad', icon: 'ri-group-line' },
  { id: 'about', label: 'Acerca', icon: 'ri-information-line' },
];

interface MobileRadioProfileProps {
  station: RadioStation;
}

export default function MobileRadioProfile({ station }: MobileRadioProfileProps) {
  const { play } = useAudioPlayer();
  const [activeTab, setActiveTab] = useState('feed');
  const [following, setFollowing] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const profile = radioProfiles[station.id];
  const team = getRadioTeam(station.id);
  const followers = 12500 + station.listeners * 2;
  const programsCount = profile?.schedule?.length || 4;

  const techStatus = station.technicalStreamStatus;
  const statusColors = technicalStreamStatusColors[techStatus];
  const statusLabel = technicalStreamStatusShortLabels[techStatus];

  const formatNumber = (n: number) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toString();
  };

  const handlePlay = () => {
    if (isTerminalStatus(techStatus) || isTransientStatus(techStatus)) {
      SwalAlert.info('Esta radio no está disponible en este momento.', 'No disponible');
      return;
    }
    play(station);
  };

  const getPlayButtonStyle = () => {
    if (canPlayStream(techStatus)) return 'bg-accent-500 text-background-50 dark:text-foreground-950';
    if (shouldFallbackToRfw(techStatus)) return 'bg-amber-500 text-white';
    return 'bg-foreground-300 text-white';
  };

  const getPlayIcon = () => {
    if (canPlayStream(techStatus)) return 'ri-headphone-fill';
    if (shouldFallbackToRfw(techStatus)) return 'ri-shuffle-line';
    return 'ri-forbid-2-line';
  };

  const isPlayDisabled = isTerminalStatus(techStatus) || isTransientStatus(techStatus);

  const coverUrl = 'https://readdy.ai/api/search-image?query=Radio%20station%20cover%20banner%20background%20with%20abstract%20sound%20waves%20and%20colorful%20equalizer%20bars%2C%20warm%20vibrant%20orange%20and%20amber%20tones%20gradient%2C%20modern%20dynamic%20broadcast%20visual%2C%20music%20energy%20flow%2C%20artistic%20digital%20background%2C%20wide%20panoramic%20composition%2C%20clean%20minimal%20style&width=1440&height=400&seq=cover-radio-v3&orientation=landscape';

  return (
    <div className="md:hidden min-h-screen bg-background-50 pb-24">
      {/* Compact Cover */}
      <div className="relative w-full h-[180px] bg-background-200/70 overflow-hidden">
        <img src={coverUrl} alt={station.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent"></div>
        <Link
          to="/app"
          className="absolute top-3 left-3 z-20 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center cursor-pointer"
        >
          <i className="ri-arrow-left-line text-white text-lg"></i>
        </Link>
      </div>

      {/* Logo + Info */}
      <div className="px-4 -mt-12 relative z-10">
        <div className="flex items-end gap-3 mb-3">
          <div className="w-20 h-20 rounded-full border-[3px] border-background-50 bg-background-50 overflow-hidden shadow-lg shrink-0">
            <img src={station.logoUrl} alt={station.name} className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 min-w-0 pb-1">
            <h1 className="text-lg font-bold text-foreground-950 leading-tight">{station.name}</h1>
            <p className="text-xs text-foreground-500">{station.frequency} · {station.city}</p>
            <div className="flex items-center gap-1.5 mt-1">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${statusColors?.bg || 'bg-foreground-100'} ${statusColors?.text || 'text-foreground-600'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${statusColors?.dot || 'bg-foreground-300'}`}></span>
                {statusLabel}
              </span>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3 flex-wrap">
          <span><strong className="text-foreground-700">{formatNumber(followers)}</strong> seguidores</span>
          <span><strong className="text-foreground-700">{formatNumber(station.listeners)}</strong> oyentes</span>
          <span><strong className="text-foreground-700">{programsCount}</strong> programas</span>
        </div>

        {/* Actions Row */}
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={handlePlay}
            disabled={isPlayDisabled}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${getPlayButtonStyle()} ${isPlayDisabled ? 'opacity-50' : ''}`}
          >
            <i className={`${getPlayIcon()} text-sm`}></i>
            Escuchar
          </button>
          <button
            onClick={() => setFollowing(!following)}
            className={`flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${following ? 'bg-secondary-100 text-secondary-700 border border-secondary-200' : 'bg-primary-500 text-background-50 dark:text-foreground-950'}`}
          >
            <i className={`${following ? 'ri-user-follow-fill' : 'ri-user-follow-line'} text-sm`}></i>
            {following ? 'Siguiendo' : 'Seguir'}
          </button>
          <LikeButton entityId={station.id} entityType="radio" initialLikes={0} initialLiked={false} size="sm" />
          <button
            onClick={() => setShowShareModal(true)}
            className="w-9 h-9 rounded-xl bg-background-100 border border-background-200/70 flex items-center justify-center cursor-pointer text-foreground-600 hover:bg-background-200/70 transition-colors"
          >
            <i className="ri-share-forward-line text-sm"></i>
          </button>
        </div>

        {/* Current metadata / program */}
        {station.currentMetadata && (
          <p className="text-xs text-foreground-400 mb-3 flex items-center gap-1.5 bg-background-100 rounded-lg px-3 py-2">
            <i className="ri-music-line text-[11px]"></i>
            <span className="truncate">{station.currentMetadata}</span>
          </p>
        )}
      </div>

      {/* Horizontal Scrollable Tabs */}
      <div className="sticky top-0 z-20 bg-background-50 border-b border-background-200/70">
        <div className="flex items-center gap-0 overflow-x-auto scrollbar-hide px-2">
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold whitespace-nowrap border-b-2 transition-all cursor-pointer shrink-0 ${isActive ? 'border-primary-500 text-primary-600' : 'border-transparent text-foreground-500'}`}
              >
                <i className={`${tab.icon} text-sm`}></i>
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="px-4 py-4">
        {activeTab === 'feed' && <RadioFeedSection station={station} />}
        {activeTab === 'schedule' && <MobileScheduleTab station={station} />}
        {activeTab === 'content' && <MobileContentTab station={station} />}
        {activeTab === 'events' && <RadioEventosSection station={station} />}
        {activeTab === 'contests' && <RadioConcursosSection station={station} />}
        {activeTab === 'community' && <SeccionComunidad station={station} />}
        {activeTab === 'about' && <SeccionAcerca station={station} />}
      </div>

      {/* Share Modal */}
      <ShareModal
        entityName={station.name}
        entitySubtitle={station.frequency}
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />
    </div>
  );
}

/* ─── Mobile Schedule Tab ─── */

function MobileScheduleTab({ station }: { station: RadioStation }) {
  const profile = radioProfiles[station.id];

  if (!profile || profile.schedule.length === 0) {
    return (
      <div className="py-12 text-center">
        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-foreground-100 flex items-center justify-center">
          <i className="ri-calendar-2-line text-2xl text-foreground-400"></i>
        </div>
        <p className="text-sm text-foreground-500">Programación no disponible</p>
      </div>
    );
  }

  return (
    <section className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-background-200/70">
        <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
          <i className="ri-time-line text-accent-600 text-sm"></i>
        </div>
        <div>
          <h3 className="text-sm font-bold text-foreground-950">Programación</h3>
          <p className="text-[11px] text-foreground-500">{profile.schedule.length} programas</p>
        </div>
      </div>
      <div className="divide-y divide-background-200/70">
        {profile.schedule.map((entry, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-3 hover:bg-background-100/50 transition-colors">
            <div className="flex flex-col items-start min-w-[80px] shrink-0">
              <span className="text-[11px] font-semibold text-foreground-600 whitespace-nowrap">{entry.day}</span>
              <span className="text-xs font-bold text-foreground-950 whitespace-nowrap">{entry.timeRange}</span>
            </div>
            <div className="w-px self-stretch bg-background-200/70"></div>
            <div className="flex-1 min-w-0">
              <h4 className="text-xs font-bold text-foreground-950">{entry.program}</h4>
              <p className="text-[11px] text-foreground-500">con {entry.host}</p>
            </div>
            <button className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-background-100 border border-background-200/70 text-[10px] font-semibold text-foreground-600 hover:bg-background-200/70 cursor-pointer transition-colors whitespace-nowrap shrink-0">
              <i className="ri-notification-3-line text-xs"></i>
              Recordar
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─── Mobile Content Tab (Photos / Videos / Lives & Replays) ─── */

type ContentSubTab = 'photos' | 'videos' | 'lives';

function MobileContentTab({ station }: { station: RadioStation }) {
  const [subTab, setSubTab] = useState<ContentSubTab>('photos');
  const photos = getStationPhotos(station.id);
  const videos = getStationVideos(station.id);
  const lives = getStationLives(station.id);

  const subTabs: { key: ContentSubTab; label: string; icon: string; count: number }[] = [
    { key: 'photos', label: 'Fotos', icon: 'ri-image-line', count: photos.length },
    { key: 'videos', label: 'Videos', icon: 'ri-video-line', count: videos.length },
    { key: 'lives', label: 'Lives', icon: 'ri-live-line', count: lives.length },
  ];

  const formatNumber = (n: number) => {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toString();
  };

  return (
    <div className="space-y-4">
      {/* Sub-tabs */}
      <div className="flex items-center gap-1 bg-background-100 rounded-full p-0.5 overflow-x-auto scrollbar-hide">
        {subTabs.map((st) => {
          const isActive = subTab === st.key;
          return (
            <button
              key={st.key}
              onClick={() => setSubTab(st.key)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer transition-all whitespace-nowrap shrink-0 ${isActive ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
            >
              <i className={`${st.icon} text-xs`}></i>
              {st.label}
              <span className={`text-[10px] ${isActive ? 'text-foreground-400' : 'text-foreground-400'}`}>{st.count}</span>
            </button>
          );
        })}
      </div>

      {/* Photos Grid */}
      {subTab === 'photos' && (
        <>
          {photos.length === 0 ? (
            <div className="py-12 text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-foreground-100 flex items-center justify-center">
                <i className="ri-image-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-sm text-foreground-500">Sin fotos aún</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-1.5">
              {photos.slice(0, 15).map((photo) => (
                <div key={photo.id} className="aspect-square rounded-lg overflow-hidden bg-background-200/70 relative group cursor-pointer">
                  <img src={photo.url} alt={photo.caption} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/0 group-active:bg-black/20 transition-colors"></div>
                  <div className="absolute bottom-1 left-1 right-1">
                    <div className="flex items-center gap-1 text-white/90 text-[10px]">
                      <i className="ri-heart-fill text-[9px]"></i>
                      <span>{photo.likes}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Videos List */}
      {subTab === 'videos' && (
        <>
          {videos.length === 0 ? (
            <div className="py-12 text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-foreground-100 flex items-center justify-center">
                <i className="ri-video-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-sm text-foreground-500">Sin videos aún</p>
            </div>
          ) : (
            <div className="space-y-2">
              {videos.slice(0, 8).map((video) => (
                <div key={video.id} className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70 cursor-pointer hover:border-background-300/60 transition-all">
                  <div className="relative w-[100px] aspect-video rounded-lg overflow-hidden bg-background-200/70 shrink-0">
                    <img src={video.thumbnailUrl} alt={video.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                      <div className="w-7 h-7 rounded-full bg-white/90 flex items-center justify-center">
                        <i className="ri-play-fill text-sm text-foreground-950 ml-px"></i>
                      </div>
                    </div>
                    <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-black/70 text-white text-[9px] font-medium">{video.duration}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-foreground-950 line-clamp-2">{video.title}</h4>
                    <p className="text-[11px] text-foreground-500 mt-0.5">{formatNumber(video.views)} vistas</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Lives / Replays */}
      {subTab === 'lives' && (
        <>
          {lives.length === 0 ? (
            <div className="py-12 text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-foreground-100 flex items-center justify-center">
                <i className="ri-live-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-sm text-foreground-500">Sin lives aún</p>
            </div>
          ) : (
            <div className="space-y-2">
              {lives.map((live) => {
                const isLive = live.status === 'en_vivo';
                const isScheduled = live.status === 'programado';
                return (
                  <div key={live.id} className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70 cursor-pointer hover:border-background-300/60 transition-all">
                    <div className="relative w-[100px] aspect-video rounded-lg overflow-hidden bg-background-200/70 shrink-0">
                      <img src={live.imageUrl} alt={live.title} className="w-full h-full object-cover" />
                      {isLive && (
                        <div className="absolute top-1.5 left-1.5 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold whitespace-nowrap">
                          <span className="w-1 h-1 rounded-full bg-white animate-pulse"></span>
                          EN VIVO
                        </div>
                      )}
                      {isScheduled && (
                        <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[9px] font-bold whitespace-nowrap">
                          Próximo
                        </div>
                      )}
                      <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-black/70 text-white text-[9px]">{live.duration}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-foreground-950 line-clamp-2">{live.title}</h4>
                      <p className="text-[11px] text-foreground-500 mt-0.5">{live.hostName}</p>
                      {isLive && <p className="text-[10px] text-foreground-400 mt-0.5">{formatNumber(live.viewers)} espectadores</p>}
                      {isScheduled && live.scheduledDate && (
                        <p className="text-[10px] text-foreground-400 mt-0.5">{live.scheduledDate} · {live.scheduledTime}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}